import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell,
} from 'recharts';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders() {
  const token = JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state?.token ?? '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtUGX(n: number) {
  return 'UGX ' + Math.round(n).toLocaleString('en-UG');
}

function fmtShort(n: number) {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(Math.round(n));
}

function Skeleton({ h = 56 }: { h?: number }) {
  return (
    <div style={{
      height: h, borderRadius: 10,
      background: 'var(--surface2)', animation: 'pulse 1.5s infinite',
    }} />
  );
}

function ChartTooltipContent({ active, payload, label }: { active?: boolean; payload?: { value: number; name?: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="card" style={{ padding: '8px 12px', fontSize: 12 }}>
      {label && <p style={{ color: 'var(--text-3)', marginBottom: 2 }}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ fontWeight: 600, color: 'var(--text)' }}>{p.name ? `${p.name}: ` : ''}{fmtUGX(p.value)}</p>
      ))}
    </div>
  );
}

const TICK = { fontSize: 11, fill: 'var(--text-3)' };
const GRID = { strokeDasharray: '3 3', stroke: 'var(--border)' };
const COLORS = ['var(--accent)', '#0ea5e9', '#10b981', '#6366f1', '#f43f5e', '#8b5cf6'];

export function ReportsPage() {
  const today = new Date();
  const sevenDaysAgo = new Date(today.getTime() - 7 * 86400000).toISOString().slice(0, 16);
  const now = today.toISOString().slice(0, 16);
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const lastDay = today.toISOString().slice(0, 10);

  const [activeTab, setActiveTab] = useState<'revenue' | 'waiters' | 'categories' | 'delivery'>('revenue');
  const [from, setFrom] = useState(sevenDaysAgo);
  const [to, setTo] = useState(now);
  const [reportFrom, setReportFrom] = useState(firstDay);
  const [reportTo, setReportTo] = useState(lastDay);

  const { data: hourlyData = [], isLoading: hourlyLoading } = useQuery<{ label: string; revenue: number; orders: number }[]>({
    queryKey: ['hourly-revenue', from, to],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/reports/hourly/range?from=${from.slice(0, 10)}&to=${to.slice(0, 10)}`, { headers: authHeaders() });
      const j = await res.json();
      return (j.data?.buckets ?? []).map((r: { label: string; totalRevenueUgx: string; orderCount: number }) => ({
        label: r.label,
        revenue: Number(r.totalRevenueUgx),
        orders: r.orderCount,
      }));
    },
    enabled: activeTab === 'revenue',
  });

  const { data: waiters = [], isLoading: waitersLoading } = useQuery<{ id: string; name: string; totalRevenue: number; totalOrders: number; avgOrderValue: number }[]>({
    queryKey: ['waiter-performance', reportFrom, reportTo],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/reports/waiter-performance?from=${reportFrom}&to=${reportTo}`, { headers: authHeaders() });
      const j = await res.json();
      return (j.data?.leaderboard ?? []).map((w: { waiterId: string; waiterName: string; totalRevenueUgx: string; orderCount: number; avgOrderValueUgx: string }) => ({
        id: w.waiterId,
        name: w.waiterName,
        totalRevenue: Number(w.totalRevenueUgx),
        totalOrders: w.orderCount,
        avgOrderValue: Number(w.avgOrderValueUgx),
      }));
    },
    enabled: activeTab === 'waiters',
  });

  const { data: categoryData = [], isLoading: catLoading } = useQuery<{ categoryId: string; categoryName: string; revenue: number; costPct: string }[]>({
    queryKey: ['food-cost', reportFrom, reportTo],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/reports/food-cost?from=${reportFrom}&to=${reportTo}`, { headers: authHeaders() });
      const j = await res.json();
      return (j.data?.perItem ?? []).map((i: { menuItemId: string; name: string; sellingPriceUgx: string; costPct: string }) => ({
        categoryId: i.menuItemId,
        categoryName: i.name,
        revenue: Number(i.sellingPriceUgx),
        costPct: i.costPct,
      }));
    },
    enabled: activeTab === 'categories',
  });

  const { data: deliveryData } = useQuery<{ total: number; delivered: number; failed: number; successRate: string; avgDeliveryMins: number | null }>({
    queryKey: ['delivery-performance', reportFrom, reportTo],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/delivery/reports/performance?from=${reportFrom}&to=${reportTo}`, { headers: authHeaders() });
      const j = await res.json();
      return j.data?.summary ?? null;
    },
    enabled: activeTab === 'delivery',
  });

  const totalRevenue = hourlyData.reduce((s, r) => s + r.revenue, 0);
  const totalOrders = hourlyData.reduce((s, r) => s + r.orders, 0);
  const peakHour = hourlyData.reduce<{ hour: string; revenue: number } | null>((max, r) => {
    const row = { hour: r.label, revenue: r.revenue };
    return !max || row.revenue > max.revenue ? row : max;
  }, null);

  return (
    <div>
      <div className="page-header" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Revenue trends, staff performance, and category breakdown</p>
        </div>
        {activeTab === 'revenue' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <span style={{ color: 'var(--text-3)' }}>From</span>
            <input className="input" type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} style={{ width: 160 }} />
            <span style={{ color: 'var(--text-3)' }}>To</span>
            <input className="input" type="datetime-local" value={to} onChange={e => setTo(e.target.value)} style={{ width: 160 }} />
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <span style={{ color: 'var(--text-3)' }}>From</span>
            <input className="input" type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)} style={{ width: 140 }} />
            <span style={{ color: 'var(--text-3)' }}>To</span>
            <input className="input" type="date" value={reportTo} onChange={e => setReportTo(e.target.value)} style={{ width: 140 }} />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 20 }}>
        {(['revenue', 'waiters', 'categories', 'delivery'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '6px 18px', borderRadius: 8, fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
              background: activeTab === tab ? 'var(--surface)' : 'transparent',
              color: activeTab === tab ? 'var(--text)' : 'var(--text-3)',
              border: 'none', cursor: 'pointer',
              boxShadow: activeTab === tab ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.12s',
            }}
          >
            {tab === 'revenue' ? 'Revenue' : tab === 'waiters' ? 'Waiters' : tab === 'categories' ? 'Categories' : 'Delivery'}
          </button>
        ))}
      </div>

      {/* Revenue tab */}
      {activeTab === 'revenue' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            {[
              { label: 'Total Revenue', value: fmtUGX(totalRevenue) },
              { label: 'Total Orders', value: totalOrders.toLocaleString() },
              { label: 'Peak Hour', value: peakHour ? `${peakHour.hour} (${fmtUGX(peakHour.revenue)})` : '—' },
            ].map(kpi => (
              <div key={kpi.label} className="stat-card">
                <p className="stat-label">{kpi.label}</p>
                <p className="stat-value" style={{ fontFamily: 'var(--font-mono)' }}>{kpi.value}</p>
              </div>
            ))}
          </div>
          <div className="card">
            <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 16 }}>Revenue by Hour</p>
            {hourlyLoading ? <Skeleton h={220} /> : hourlyData.length === 0 ? (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 13 }}>No data for this period.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={hourlyData}>
                  <defs>
                    <linearGradient id="revGradR" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.18} />
                      <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="label" tick={TICK} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={fmtShort} tick={TICK} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltipContent />} />
                  <Area type="monotone" dataKey="revenue" stroke="var(--accent)" strokeWidth={2} fill="url(#revGradR)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="card">
            <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 16 }}>Orders by Hour</p>
            {hourlyLoading ? <Skeleton h={180} /> : hourlyData.length === 0 ? (
              <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 13 }}>No data.</div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={hourlyData} barSize={20}>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="label" tick={TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={TICK} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey="orders" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {/* Waiters tab */}
      {activeTab === 'waiters' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {waitersLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} />)}
            </div>
          ) : waiters.length === 0 ? (
            <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No waiter data for this period.</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="card">
                  <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 16 }}>Revenue by Waiter</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={waiters.slice(0, 10)} layout="vertical" barSize={16}>
                      <CartesianGrid {...GRID} horizontal={false} />
                      <XAxis type="number" tickFormatter={fmtShort} tick={TICK} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fill: 'var(--text-2)' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="totalRevenue" fill="var(--accent)" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="card">
                  <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 16 }}>Orders by Waiter</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={waiters.slice(0, 10)} layout="vertical" barSize={16}>
                      <CartesianGrid {...GRID} horizontal={false} />
                      <XAxis type="number" tick={TICK} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fill: 'var(--text-2)' }} axisLine={false} tickLine={false} />
                      <Tooltip />
                      <Bar dataKey="totalOrders" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Waiter</th>
                        <th style={{ textAlign: 'right' }}>Revenue</th>
                        <th style={{ textAlign: 'right' }}>Orders</th>
                        <th style={{ textAlign: 'right' }}>Avg Order</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waiters.map((w, i) => (
                        <tr key={w.id}>
                          <td style={{ color: 'var(--text-3)', fontWeight: 600 }}>
                            {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                          </td>
                          <td style={{ fontWeight: 500, color: 'var(--text)' }}>{w.name}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(w.totalRevenue)}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-2)' }}>{w.totalOrders}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(w.avgOrderValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Categories tab */}
      {activeTab === 'categories' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {catLoading ? (
            <Skeleton h={260} />
          ) : categoryData.length === 0 ? (
            <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No category data for this period.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="card">
                <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 16 }}>Selling Price Distribution</p>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      dataKey="revenue"
                      nameKey="categoryName"
                      cx="50%" cy="50%"
                      outerRadius={100}
                      label={({ categoryName, percent }: { categoryName: string; percent: number }) => `${categoryName} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {categoryData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltipContent />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <p style={{ padding: '16px 20px 0', fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>Menu Item Food Cost</p>
                <div className="table-wrap" style={{ marginTop: 8 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th style={{ textAlign: 'right' }}>Selling Price</th>
                        <th style={{ textAlign: 'right' }}>Food Cost %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryData.map((cat, i) => (
                        <tr key={cat.categoryId}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 10, height: 10, borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                              <span style={{ fontWeight: 500, color: 'var(--text)' }}>{cat.categoryName}</span>
                            </div>
                          </td>
                          <td style={{ textAlign: 'right', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(cat.revenue)}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-2)' }}>{cat.costPct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delivery tab */}
      {activeTab === 'delivery' && (
        !deliveryData ? (
          <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No delivery data for this period.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {[
              { label: 'Total Deliveries', value: String(deliveryData.total) },
              { label: 'Delivered', value: String(deliveryData.delivered) },
              { label: 'Avg Delivery Time', value: deliveryData.avgDeliveryMins != null ? `${Math.round(deliveryData.avgDeliveryMins)} min` : '—' },
              { label: 'Success Rate', value: deliveryData.successRate },
            ].map(kpi => (
              <div key={kpi.label} className="stat-card">
                <p className="stat-label">{kpi.label}</p>
                <p className="stat-value">{kpi.value}</p>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
